import React, { FC, useMemo } from 'react'
import SALink from 'components/SALink'
import { capitalizeString } from 'lib/helpers'
import { slugify } from 'lib/slugify'
import FacetConnector, { ConnectorProps as FacetProps } from '../connectors/Facet'
import styles from './Breadcrumb.module.scss'
import Skeleton from 'components/SA/Skeleton'
import useAvoidLayoutShift from 'hooks/useAvoidLayoutShift'

const facetAttributeValueReadable = (item: string): string =>
  item
    .toString()
    .replace(/^all$/i, '')
    .replace(/^prints$/i, 'Art Prints')
    .replace(/^open$/i, 'Open Editions')
    .replace(/^limited$/i, 'Limited Edtions')
    .replace(/^rag$/i, 'Fine Art Paper')
    .replace(/^canvas$/i, 'Canvas')
    .replace(/^photo$/i, 'Photo Paper')

const createLinkTitle = ({
  hasPrints,
  breadcrumbFacets,
  index,
}: {
  hasPrints: boolean
  breadcrumbFacets: Array<string>
  index: number
}): string => {
  // Append `category` & `print` to end and join everything together
  // ['print', 'Photography', 'Documentary', 'Landscape', 'Digital']
  // Results => 'Documentary Landscape Digital Photography Art Prints'
  let printsTitle = ''
  let categoryTitle = ''
  const facetNames: string =
    breadcrumbFacets
      .slice(1, index + 1)
      .filter(Boolean)
      .join(' ') || ''

  if (hasPrints) {
    printsTitle =
      breadcrumbFacets
        .slice(0, 1)
        .filter(Boolean)
        .join('') || ''
    categoryTitle =
      breadcrumbFacets
        .slice(1, 1)
        .filter(Boolean)
        .join('') || ''
  } else {
    printsTitle = ''
    categoryTitle = breadcrumbFacets
      .slice(0, 1)
      .filter(Boolean)
      .join('')
  }

  const titleItems: Array<string> = [facetNames, printsTitle, categoryTitle]
    .filter(Boolean)
    .map(facetAttributeValueReadable)
  return titleItems.join(' ')
}

const BrowseBreadcrumb: FC<FacetProps> = ({ breadcrumbFacets, hasPrints }) => {
  const breadcrumbLinks = useMemo(
    () =>
      breadcrumbFacets.map((item, index) => ({
        name: capitalizeString(item),
        url: `/${breadcrumbFacets
          .slice(0, index + 1)
          .map(slugify)
          .join('/')}`,
        title: `See artworks in ${createLinkTitle({
          hasPrints,
          breadcrumbFacets,
          index,
        })}`,
        link: index < breadcrumbFacets.length - 1,
      })),
    [breadcrumbFacets]
  )

  const { isSkeletonVisible } = useAvoidLayoutShift()

  return (
    <nav className={styles.breadcrumbContainer}>
      {isSkeletonVisible ? (
        <Skeleton width='150px' height='21px' />
      ) : (
        <ul data-type='breadcrumbUL'>
          <li>
            <SALink href='/all' title='See all artworks'>
              All Artworks
            </SALink>
          </li>
          {breadcrumbLinks.map(({ name, url, title, link }, index) =>
            link ? (
              <li key={`bc-${index}`}>
                <SALink title={title} href={url} key={`${name}-${index}`}>
                  {name}
                </SALink>
              </li>
            ) : (
              <li key={`bc-${index}`}>
                <span key={`${name}-${index}`}>{name}</span>
              </li>
            )
          )}
        </ul>
      )}
    </nav>
  )
}

export default FacetConnector(BrowseBreadcrumb)
